import React from 'react';
import {
    FileText,
    Eye,
    Download,
    Loader2,
} from 'lucide-react';
import type { Customer, PensionFund } from '../../types';

export interface WorkflowDocumentsTabProps {
    customer: Customer;
    getFileUrl: (filePath: string | undefined) => string;
    // Caisses tab data
    activeTab: 'actions' | 'info' | 'caisses' | 'documents' | 'history';
    pensionFundsData: PensionFund[] | undefined;
    isPensionFundsLoading: boolean;
}

const WorkflowDocumentsTab: React.FC<WorkflowDocumentsTabProps> = ({
    customer,
    getFileUrl,
    activeTab,
    pensionFundsData,
    isPensionFundsLoading,
}) => {
    if (activeTab === 'caisses') {
        return (
            <div className="bg-white rounded-lg shadow">
                {isPensionFundsLoading ? (
                    <div className="flex items-center justify-center p-12">
                        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
                    </div>
                ) : pensionFundsData && pensionFundsData.length > 0 ? (
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Institution
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Type
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Montant
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Statut
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {pensionFundsData.map((pf: PensionFund) => (
                                    <tr key={pf.id}>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {pf.fund_name || pf.fondation}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            LPP / Libre Passage
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {pf.amount ? `CHF ${parseFloat(pf.amount).toLocaleString('fr-CH')}` : '-'}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                                pf.status === 'verified' ? 'bg-green-100 text-green-800' :
                                                pf.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                                'bg-gray-100 text-gray-800'
                                            }`}>
                                                {pf.status === 'verified' ? 'Vérifié' :
                                                 pf.status === 'pending' ? 'En attente' : pf.status || 'Inconnu'}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="text-center py-12 text-gray-500">
                        Aucune caisse de pension trouvée
                    </div>
                )}
            </div>
        );
    }

    const renderDocumentCard = (doc: { label: string; field: string }) => {
        const value = customer[doc.field as keyof Customer] as string | undefined;
        const hasDocument = !!value;

        return (
            <div
                key={doc.field}
                className={`relative border-2 rounded-xl p-5 transition-all duration-200 ${
                    hasDocument
                        ? 'border-green-200 bg-green-50 hover:shadow-md'
                        : 'border-gray-200 bg-gray-50 hover:border-gray-300'
                }`}
            >
                <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${hasDocument ? 'bg-green-100' : 'bg-gray-200'}`}>
                            <FileText className={`h-5 w-5 ${hasDocument ? 'text-green-600' : 'text-gray-400'}`} />
                        </div>
                        <div>
                            <h4 className="font-semibold text-gray-900 text-sm">{doc.label}</h4>
                            <p className={`text-xs mt-1 ${hasDocument ? 'text-green-600' : 'text-red-600'}`}>
                                {hasDocument ? 'Disponible' : 'Non fourni'}
                            </p>
                        </div>
                    </div>
                </div>

                <div className="flex flex-wrap gap-2 mt-4">
                    {hasDocument ? (
                        <>
                            <a
                                href={getFileUrl(value)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-primary-700 bg-primary-50 rounded-lg hover:bg-primary-100 transition-colors"
                            >
                                <Eye className="h-3.5 w-3.5" />
                                Voir
                            </a>
                            <a
                                href={getFileUrl(value)}
                                download
                                className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
                            >
                                <Download className="h-3.5 w-3.5" />
                                Télécharger
                            </a>
                        </>
                    ) : (
                        <span className="text-xs text-gray-400 italic">Aucun document</span>
                    )}
                </div>
            </div>
        );
    };

    const identityDocs = [
        { label: "Pièce d'identité recto", field: 'photo' },
        { label: "Pièce d'identité verso", field: 'photo2' },
    ];

    const contractualDocs = [
        { label: 'Procuration', field: 'procuration' },
        { label: 'Demande centrale', field: 'libre_passage' },
    ];

    const institutionalDocs = [
        { label: 'Demande supplétive', field: 'demande_suppletive' },
        { label: 'Réponse supplétive', field: 'reponse_suppletive' },
        { label: 'Réponse centrale', field: 'reponse_centrale' },
    ];

    // Documents tab
    return (
        <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-6">Documents d'identité</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
                {identityDocs.map(renderDocumentCard)}
            </div>

            <h3 className="text-xl font-semibold text-gray-900 mb-6 mt-10">Documents administratifs</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
                {contractualDocs.map(renderDocumentCard)}
            </div>

            <h3 className="text-xl font-semibold text-gray-900 mb-6 mt-10">Réponses institutions</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {institutionalDocs.map(renderDocumentCard)}
            </div>
        </div>
    );
};

export default WorkflowDocumentsTab;
